import React from "react";
import { FaBrain, FaCheckCircle, FaChartLine, FaGlobe, FaExclamationTriangle, FaInfoCircle, FaTicketAlt, FaChartBar, FaCertificate, FaUser, FaFlag, FaMapPin, FaBox, FaPhone } from "react-icons/fa";
import { GiWeight } from "react-icons/gi";
import { MdErrorOutline } from "react-icons/md";
import { CheckCircle, Box } from "react-feather";

interface AIAgentBoxProps {
    title: string;
    description: string;
    features: Array<{
        icon: React.ReactNode;
        text: string;
        iconColor?: string;
    }>;
    buttonText?: string;
    onButtonClick?: () => void;
    disabled?: boolean;
    theme?: 'purple' | 'blue' | 'green' | 'orange';
}

const AIAgentBox: React.FC<AIAgentBoxProps> = ({
    title,
    description,
    features,
    buttonText = "Run AI Analysis",
    onButtonClick,
    disabled = false,
    theme = 'purple'
}) => {
    const getThemeStyles = () => {
        switch (theme) {
            case 'blue':
                return {
                    background: 'white',
                    border: '1px solid #E5E7EB',
                    titleColor: '#0d6efd',
                    buttonBg: '#0d6efd',
                    buttonHover: '#0b5ed7'
                };
            case 'green':
                return {
                    background: 'white',
                    border: '1px solid #ddd',
                    titleColor: '#28a745',
                    buttonBg: '#28a745',
                    buttonHover: '#218838'
                };
            case 'orange':
                return {
                    background: '#fff7ed',
                    border: '1.5px solid #ffcead',
                    titleColor: '#ff7e39',
                    buttonBg: '#ff7e39',
                    buttonHover: '#e66a00'
                };
            default: // purple
                return {
                    background: '#f9fafb',
                    border: '1px solid #e5e7eb',
                    titleColor: '#664de5',
                    buttonBg: '#664de5',
                    buttonHover: '#503ad4'
                };
        }
    };

    const themeStyles = getThemeStyles();

    return (
        <div
            className="mb-4 shadow-sm"
            style={{
                background: themeStyles.background,
                border: themeStyles.border,
                borderRadius: "16px",
                boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
                padding: "24px",
                minHeight: "200px",
                width: "100%",
                maxWidth: "100%"
            }}
        >
            <div className="d-flex align-items-center mb-3">
                <FaBrain className="me-2" style={{ color: themeStyles.titleColor }} size={24} />
                <h5 className="fw-bold mb-0 text-dark">{title}</h5>
            </div>

            <p style={{ color: "#555", fontSize: "15px" }} className="mb-4">
                <FaInfoCircle style={{ marginRight: "6px", color: themeStyles.titleColor }} />
                {description}
            </p>

            <div className="row text-dark fw-medium mb-4">
                {features.map((feature, index) => (
                    <div key={index} className="col-lg-3 col-md-6 d-flex align-items-center gap-2 mb-2">
                        <span style={{ color: feature.iconColor || themeStyles.titleColor, minWidth: "20px" }}>
                            {feature.icon}
                        </span>
                        <span style={{ fontSize: "14px" }}>{feature.text}</span>
                    </div>
                ))}
            </div>

            <button
                className="d-flex align-items-center gap-2 fw-bold border-0"
                style={{
                    backgroundColor: themeStyles.buttonBg,
                    color: "#fff",
                    borderRadius: "8px",
                    fontSize: "15px",
                    padding: "12px 24px",
                    minWidth: "160px",
                    height: "44px",
                    boxShadow: "0px 3px 6px rgba(0,0,0,0.1)",
                    transition: "background-color 0.3s ease"
                }}
                onClick={onButtonClick}
                disabled={disabled}
                onMouseEnter={(e) => {
                    if (!disabled) {
                        e.currentTarget.style.backgroundColor = themeStyles.buttonHover;
                    }
                }}
                onMouseLeave={(e) => {
                    if (!disabled) {
                        e.currentTarget.style.backgroundColor = themeStyles.buttonBg;
                    }
                }}
            >
                <FaBrain /> {buttonText}
            </button>
        </div>
    );
};

export default AIAgentBox;
